import { LitElement, html, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { experiencesStyles } from './experiences.styles';
import { SbkButton } from '../button/button';

interface Experience {
    category: string;
    text: string;
    author?: string;
    crdate: number;
}

interface Disturber {
    title: string;
    text: string;
    button: string;
    author?: string;
}

@customElement('sbk-experiences')
export class Experiences extends LitElement {
    @property({ type: String }) experiences: string = '[]'; // JSON string
    @property({ type: String }) disturbers: string = '[]'; // JSON string

    @state() private parsedExperiences: Experience[] = [];
    @state() private parsedDisturbers: Disturber[] = [];
    @state() private displayedExperiences: (Experience | Disturber)[] = [];
    @state() private currentPage = 1;
    @state() private currentCategory: string = 'All';

    private itemsPerPage: number = 5;

    static get styles() {
        return experiencesStyles;
    }

    constructor() {
        super();
        this.handleCategoryChange = this.handleCategoryChange.bind(this);
    }

    connectedCallback() {
        super.connectedCallback();
        this.parseData();
        document.addEventListener("category-change", this.handleCategoryChange);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener("category-change", this.handleCategoryChange);
    }

    private handleCategoryChange = (event: Event) => {
        const customEvent = event as CustomEvent;
        this.setCategory(customEvent.detail.category);
    };

    private parseData() {
        try {
            this.parsedExperiences = JSON.parse(this.experiences) || [];
            this.parsedDisturbers = JSON.parse(this.disturbers) || [];

            // Sort experiences by creation date (newest first)
            this.parsedExperiences.sort((a, b) => b.crdate - a.crdate);

            this.currentPage = 1; // Reset pagination
            this.showExperiences(true); // Load initial batch
        } catch (error) {
            console.error("Error parsing experiences or disturbers:", error);
        }
    }

    private showExperiences(initialLoad = false) {
        const startIdx = (this.currentPage - 1) * this.itemsPerPage;

        // Filter experiences based on selected category
        const filteredExperiences = this.currentCategory === 'All'
            ? this.parsedExperiences
            : this.parsedExperiences.filter(exp => exp.category === this.currentCategory);

        const newExperiences = filteredExperiences.slice(startIdx, startIdx + this.itemsPerPage);

        if (initialLoad) {
            this.displayedExperiences = newExperiences;
        } else {
            this.displayedExperiences = [...this.displayedExperiences, ...newExperiences];
        }

        this.currentPage++;

        // Every 5th batch, add a random disturber
        if (!initialLoad && this.currentPage % 5 === 0 && this.parsedDisturbers.length > 0) {
            const randomDisturber = this.parsedDisturbers[Math.floor(Math.random() * this.parsedDisturbers.length)];
            this.displayedExperiences = [...this.displayedExperiences, randomDisturber];
        }

        this.checkIfLastExperienceLoaded(filteredExperiences);
    }

    // Function to check if the last experience is loaded and disable the "Show More" button
    private checkIfLastExperienceLoaded(filteredExperiences: Array<Experience>) {
        const totalLoadedExperiences = this.displayedExperiences.length;

        // If the number of loaded experiences matches the total number of experiences, disable the "Show More" button
        const showMoreButton = this.shadowRoot?.querySelector('.toggle__show-more-link') as SbkButton;
        if (showMoreButton) {
            if (totalLoadedExperiences >= filteredExperiences.length) {
                showMoreButton.hidden = true; // Disable the button when all experiences are loaded
            } else {
                showMoreButton.hidden = false; // Enable the button if there are more experiences
            }
        }
    }

    private showMore() {
        this.showExperiences();
    }

    public setCategory(category: string) {
        this.currentCategory = category;
        this.currentPage = 1; // Reset pagination
        this.showExperiences(true);
    }

    protected updated(changedProperties: PropertyValues) {
        if (changedProperties.has('experiences') || changedProperties.has('disturbers')) {
            this.parseData();
        }
    }

    render() {
        return html`
            <div class="tiles">
                <div class="tiles__inner">
                    <div id="tiles__list" class="tiles__list">
                        ${this.displayedExperiences.map((experience, index) => html`
                            <sbk-citation-teaser 
                                class="tiles__element" 
                                id="experience${index + 1}" 
                                person-name=${experience.author ? experience.author : ''}>
                                ${'title' in experience
                ? html`<strong>${experience.title}</strong> ${experience.text} <a href="#">${experience.button}</a>`
                : html`${experience.text}`
            }
                            </sbk-citation-teaser>
                        `)}
                    </div>
                </div>
                <div class="toggle__show-more">
                    <sbk-button 
                        variant="secondary" 
                        class="toggle__show-more-link" 
                        icon="chevron-down" 
                        icon-position="right" 
                        @click=${this.showMore}>
                        Mehr laden
                    </sbk-button>
                </div>
            </div>
        `;
    }
}